import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { httpStatuses, useFetch } from '@reveni-io/reveni-ui'

import { noPaginationParams, subscriptionPlans } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useFetchPolicyRules from '@/common/hooks/useFetchPolicyRules/useFetchPolicyRules'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import { focusOnError } from '@/configuration/pages/ConfigurationPage/helpers'
import { errorTypes } from '@/configuration/pages/ConfigurationPage/ReturnPolicy/ExceptionsConfiguration/ExceptionDetailConfiguration/constants'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import { newException } from './constants'
import { hasExceptionEffect } from './helpers'
import { transformRequestCreateException, transformRequestEditException } from './transformers'

const useException = ({ selectedException, setToastInfo, id }) => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const [exception, setException] = useState(newException)
  const navigate = useNavigate()
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState({})
  const { returnPortalFeatures, fetchingReturnPortalFeatures } = useReturnPortalFeatures({
    returnPortalId: info?.selectedReturnPortal?.id,
    doInitialRequest: true,
  })

  const { fetchData: createException, loading: creatingException } = useFetch(configurationServices.createException, {
    doInitialRequest: false,
    transformRequest: transformRequestCreateException,
  })
  const { fetchData: editException, loading: editingException } = useFetch(configurationServices.editException, {
    doInitialRequest: false,
    transformRequest: transformRequestEditException,
  })
  const { policyRulesData, gettingPolicyRules } = useFetchPolicyRules({
    doInitialRequest: true,
    params: { storeId: info?.selectedStore?.id, active: true, ...noPaginationParams },
  })

  useEffect(() => {
    setException(selectedException || newException)
  }, [info, selectedException])

  const revertChanges = () => {
    setException(selectedException || newException)
    setHasChanges(false)
    setErrors({})
  }

  const onCreateException = async () => {
    try {
      await createException({ storeId: info.selectedStore.id, exception })
      setHasChanges(false)
      setToastInfo({ show: true, message: t('Guardado correctamente'), type: 'success' })
      navigate(routes.configurationPage.returnPolicy.exceptions)
    } catch (error) {
      if (error.status === httpStatuses.unprocessableEntity) {
        setToastInfo({ show: true, message: error?.response?.data?.detail, type: 'error' })
      } else {
        setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
        setErrors(error.response?.data)
        focusOnError(error.response?.data)
      }
    }
  }

  const onEditException = async () => {
    try {
      await editException({ storeId: info.selectedStore.id, exception })
      setHasChanges(false)
      setToastInfo({ show: true, message: t('Editado correctamente'), type: 'success' })
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
      setErrors(error.response?.data)
    }
  }

  const isGiftCardEnabled = () => {
    return returnPortalFeatures?.gift_card_allowed
  }

  const isStoreCreditEnabled = () => {
    return (
      !isGiftCardEnabled() &&
      returnPortalFeatures?.store_credit_allowed &&
      info?.subscription_plan !== subscriptionPlans.basic
    )
  }

  const handleChange = (key, value) => {
    setErrors({})
    setException(exception => ({ ...exception, [key]: value }))
    setHasChanges(true)
  }

  const handleChangeExpiresDate = date => {
    if (date && date.isValid()) {
      setException(exception => ({ ...exception, expires_at: date }))
      setHasChanges(true)
    }
  }

  const onSaveChanges = async () => {
    if (!hasExceptionEffect(exception)) {
      const effectsError = { [errorTypes.effectsEmpty]: true }

      focusOnError(effectsError)
      setErrors(effectsError)

      return
    }

    if (id === 'new') {
      await onCreateException()
    } else {
      await onEditException()
    }
  }

  return {
    exception,
    hasChanges,
    setException: params => {
      setException(params)
      setHasChanges(true)
    },
    revertChanges,
    onCreateException,
    creatingException,
    onEditException,
    editingException,
    errors,
    setErrors,
    isGiftCardEnabled,
    isStoreCreditEnabled,
    handleChange,
    handleChangeExpiresDate,
    onSaveChanges,
    fetchingReturnPortalFeatures,
    policyRulesData: policyRulesData?.results,
    gettingPolicyRules,
  }
}

export default useException
